import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Pending Domain Requests Component
 * Review and approve/reject domain requests from users
 */
const PendingDomainRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      const response = await fetch(`/api/admin/tools/pending-domains?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setRequests(data.requests || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch pending domain requests');
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!actionTarget) return;
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/admin/tools/pending-domains/${actionTarget.id}/approve`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Domain "${actionTarget.domain}" approved. ${data.emailsSent || 0} notification email(s) sent.`);
        setActionTarget(null);
        setActionType(null);
        fetchRequests();
      } else {
        setError(data.error || 'Failed to approve domain');
      }
    } catch (err) {
      setError('Failed to approve domain');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!actionTarget) return;
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/admin/tools/pending-domains/${actionTarget.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: rejectionReason })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Domain "${actionTarget.domain}" rejected. ${data.emailsSent || 0} notification email(s) sent.`);
        setActionTarget(null);
        setActionType(null);
        setRejectionReason('');
        fetchRequests();
      } else {
        setError(data.error || 'Failed to reject domain');
      }
    } catch (err) {
      setError('Failed to reject domain');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger'
    };
    return <Badge bg={colors[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Pending Domain Requests</h5>
            <small className="text-muted">
              Review domains submitted by users for approval
            </small>
          </div>
          <Form.Select
            style={{ width: 'auto' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </Form.Select>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          {requests.length === 0 ? (
            <Alert variant="info">
              No {statusFilter || ''} domain requests found.
            </Alert>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Sample URL</th>
                  <th>Requesters</th>
                  <th>Count</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td><code>{request.domain}</code></td>
                    <td style={{ maxWidth: '200px' }}>
                      <a
                        href={request.sample_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.875rem' }}
                        title={request.sample_url}
                      >
                        {request.sample_url?.length > 40
                          ? request.sample_url.substring(0, 40) + '...'
                          : request.sample_url}
                      </a>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {request.requester_emails?.length > 0 ? (
                        <span title={request.requester_emails.join(', ')}>
                          {request.requester_emails[0]}
                          {request.requester_emails.length > 1 && (
                            <Badge bg="secondary" className="ms-1">
                              +{request.requester_emails.length - 1}
                            </Badge>
                          )}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <Badge bg="info">{request.request_count || 1}</Badge>
                    </td>
                    <td>{statusBadge(request.status)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{formatDate(request.created_at)}</td>
                    <td>
                      {request.status === 'pending' && (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            onClick={() => {
                              setActionTarget(request);
                              setActionType('approve');
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setActionTarget(request);
                              setActionType('reject');
                              setRejectionReason('');
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {request.status === 'approved' && (
                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                          Reviewed {formatDate(request.reviewed_at)}
                        </span>
                      )}
                      {request.status === 'rejected' && (
                        <span
                          className="text-muted"
                          style={{ fontSize: '0.875rem' }}
                          title={request.rejection_reason || 'No reason provided'}
                        >
                          Rejected {formatDate(request.reviewed_at)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Approve Confirmation Modal */}
      <Modal show={actionType === 'approve'} onHide={() => { setActionTarget(null); setActionType(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Approve <strong>{actionTarget?.domain}</strong> for article processing?
          </p>
          <p className="text-muted">
            This will add the domain to the allowlist and send notification emails to all requesters
            ({actionTarget?.requester_emails?.length || 0} email{actionTarget?.requester_emails?.length !== 1 ? 's' : ''}).
          </p>
          {actionTarget?.requester_emails?.length > 0 && (
            <div className="bg-light p-2 rounded" style={{ fontSize: '0.875rem' }}>
              <strong>Requesters:</strong>
              <ul className="mb-0 mt-1">
                {actionTarget.requester_emails.map((email, idx) => (
                  <li key={idx}>{email}</li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setActionTarget(null); setActionType(null); }}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleApprove} disabled={processing}>
            {processing ? 'Approving...' : 'Approve & Notify'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal with Reason */}
      <Modal show={actionType === 'reject'} onHide={() => { setActionTarget(null); setActionType(null); setRejectionReason(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Reject <strong>{actionTarget?.domain}</strong>?
          </p>
          <p className="text-muted">
            This will notify all requesters that the domain was not approved.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Rejection Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Provide a reason for rejection (will be included in notification emails)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setActionTarget(null); setActionType(null); setRejectionReason(''); }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={processing}>
            {processing ? 'Rejecting...' : 'Reject & Notify'}
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminToolsLayout>
  );
};

export default PendingDomainRequests;
